import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { ICONS } from "@/lib/icons";
import { assetUrl } from "@/lib/api";
import { slugify } from "@/lib/slug";

export default function Services({ services }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [cat, setCat] = useState("All");
  const trackRef = useRef(null);

  const cats = useMemo(
    () => ["All", ...new Set((services || []).map((s) => s.category).filter(Boolean))],
    [services]
  );
  const filtered = useMemo(
    () => (cat === "All" ? services || [] : (services || []).filter((s) => s.category === cat)),
    [cat, services]
  );

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" });
  };

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? el.scrollLeft / max : 0);
  };

  const pickCat = (c) => {
    setCat(c);
    setProgress(0);
    trackRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  };

  const open = (s) => navigate(`/services/${slugify(s.title)}`);

  return (
    <section id="services" className="relative overflow-hidden bg-white py-24 lg:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex items-end justify-between gap-6">
          <SectionHead
            index="01"
            label="Our Offerings"
            title="Every financial rail. One counter."
            description="From Aadhaar banking to enterprise payout APIs — a full-stack fintech suite your customers already trust. Slide through and open any service for full details."
            testId="services-head"
          />
          <Reveal delay={0.1} className="mb-14 hidden shrink-0 sm:block">
            <div className="flex items-center gap-2">
              <button onClick={() => scrollBy(-1)} aria-label="Previous services" data-testid="services-prev"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E2DDD5] bg-white text-[#1B3A5C] shadow-sm transition-all hover:border-[#0E8A8A]/50 hover:text-[#0E8A8A] active:scale-95">
                <ArrowLeft size={17} />
              </button>
              <button onClick={() => scrollBy(1)} aria-label="Next services" data-testid="services-next"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E2DDD5] bg-white text-[#1B3A5C] shadow-sm transition-all hover:border-[#0E8A8A]/50 hover:text-[#0E8A8A] active:scale-95">
                <ArrowRight size={17} />
              </button>
            </div>
          </Reveal>
        </div>
      </div>

      {cats.length > 2 && (
        <div className="mx-auto mb-9 max-w-7xl px-5 lg:px-8">
          <div className="flex flex-wrap gap-x-7 gap-y-1 border-b border-[#E2DDD5]" data-testid="services-cats">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => pickCat(c)}
                data-testid={`services-cat-${c.toLowerCase()}`}
                className={`relative pb-3 pt-1 font-syne text-sm font-bold transition-colors duration-300 sm:text-base ${
                  cat === c ? "text-[#1B3A5C]" : "text-[#A8B2C1] hover:text-[#5A6578]"
                }`}
              >
                {c}
                {cat === c && (
                  <motion.span
                    layoutId="services-cat-underline"
                    className="absolute -bottom-px left-0 h-[3px] w-full rounded-full bg-gradient-to-r from-[#B8860B] to-[#0E8A8A]"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <Reveal>
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="no-scrollbar flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth px-5 pb-2 lg:px-[max(2rem,calc((100vw-80rem)/2+2rem))]"
          data-testid="services-track"
        >
          {filtered.map((s, i) => {
            const Icon = ICONS[s.icon] || ICONS.Zap;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                onClick={() => open(s)}
                onKeyDown={(e) => e.key === "Enter" && open(s)}
                role="link"
                tabIndex={0}
                className="group relative w-[85%] shrink-0 cursor-pointer snap-start overflow-hidden rounded-2xl border border-[#E2DDD5] bg-[#FAF9F5] shadow-sm transition-colors duration-300 hover:border-[#0E8A8A]/40 hover:bg-white hover:shadow-[0_24px_50px_-24px_rgba(15,27,43,0.18)] focus:outline-none focus:ring-2 focus:ring-[#0E8A8A]/40 sm:w-[47%] lg:w-[31.5%]"
                data-testid={`service-card-${i}`}
              >
                {s.imageUrl && (
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={assetUrl(s.imageUrl)}
                      alt={s.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#FAF9F5]/60 to-transparent group-hover:from-white/60" />
                  </div>
                )}
                <div className="p-7 pt-5">
                  <div className="mb-5 flex items-center justify-between">
                    <span className={`flex h-12 w-12 items-center justify-center rounded-xl bg-[#0E8A8A]/10 text-[#0E8A8A] transition-colors duration-300 group-hover:bg-[#0E8A8A]/15 ${s.imageUrl ? "-mt-12 border-4 border-white shadow-md relative z-10 bg-white" : ""}`}>
                      <Icon size={22} strokeWidth={1.8} />
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0E8A8A]">
                      Know More <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                  {s.category && (
                    <span className="mb-2 inline-block rounded-full bg-[#B8860B]/10 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#9A6A15]">
                      {s.category}
                    </span>
                  )}
                  <h3 className="font-syne text-lg font-semibold text-[#0F1B2B] sm:text-xl">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#5A6578]">{s.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Reveal>

      <div className="mx-auto mt-8 flex max-w-7xl items-center gap-4 px-5 lg:px-8">
        <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-[#EDE8DE]" data-testid="services-progress-track">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#B8860B] to-[#0E8A8A] transition-[width] duration-200"
            style={{ width: `${Math.max(8, progress * 100)}%` }}
            data-testid="services-progress-bar"
          />
        </div>
        <span className="font-mono text-[11px] text-[#8896AB]">
          {String(Math.min(filtered.length, Math.round(progress * (filtered.length - 1)) + 1)).padStart(2, "0")} / {String(filtered.length).padStart(2, "0")}
        </span>
        <div className="flex items-center gap-2 sm:hidden">
          <button onClick={() => scrollBy(-1)} aria-label="Previous services" data-testid="services-prev-mobile"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E2DDD5] bg-white text-[#1B3A5C]">
            <ArrowLeft size={15} />
          </button>
          <button onClick={() => scrollBy(1)} aria-label="Next services" data-testid="services-next-mobile"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E2DDD5] bg-white text-[#1B3A5C]">
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </section>
  );
}
